<template>
  <section class="favorite-panel">
    <template v-if="favorite">
      <header class="chat-header">
        <div>
          <h2>收藏详情</h2>
          <p>{{ favoriteTitle(favorite) }}</p>
        </div>
      </header>
      <div class="favorite-detail">
        <div class="favorite-detail-message" :class="{ recalled: favorite.recalled }">
          <span class="favorite-detail-star">★</span>
          <div class="favorite-detail-body">
            <span v-if="favorite.recalled" class="recall-mark">{{ favorite.recallStatus || '已撤回' }}</span>
            <a
              v-if="isImageFavorite(favorite)"
              class="favorite-detail-image"
              :href="favorite.attachment.url"
              target="_blank"
              rel="noreferrer"
            >
              <img :src="favorite.attachment.url" :alt="favorite.attachment.name || '图片'">
            </a>
            <a
              v-else-if="isFileFavorite(favorite)"
              class="message-file favorite-detail-file"
              :href="favorite.attachment.url"
              target="_blank"
              rel="noreferrer"
              download
            >
              <span class="message-file-icon">▤</span>
              <span class="message-file-main">
                <strong>{{ favorite.attachment.name || '文件' }}</strong>
                <small>{{ formatFileSize(favorite.attachment.size) }}</small>
              </span>
            </a>
            <p v-if="favoriteText(favorite)">{{ favoriteText(favorite) }}</p>
          </div>
          <time>{{ formatFavoriteTime(favorite.timestamp) }}</time>
        </div>
        <div class="favorite-detail-actions">
          <span v-if="notice" class="inline-save-status">{{ notice }}</span>
          <span v-else-if="error" class="inline-save-status error">{{ language.error(error) }}</span>
          <button type="button" class="ghost-inline-action" @click="copyFavorite">复制</button>
          <button type="button" class="ghost-inline-action" @click="openConversation">打开原会话</button>
          <button type="button" class="danger-inline-action" @click="removeFavorite">取消收藏</button>
        </div>
      </div>
    </template>
    <div v-else class="empty-chat">
      <strong>暂无收藏消息</strong>
      <span>右键消息气泡选择“收藏”后，会显示在这里</span>
    </div>
  </section>
</template>

<script setup>
import { useLanguageStore } from '@/stores/language';
const language = useLanguageStore();
import { computed, ref } from 'vue';
import { useConversationStore } from '@/stores/conversations';
import { useNavigationStore } from '@/stores/navigation';

const conversations = useConversationStore();
const navigation = useNavigationStore();
const favorite = computed(() => conversations.activeFavorite);
const notice = ref('');
const error = ref('');

function clearFeedback() {
  notice.value = '';
  error.value = '';
}

function favoriteTitle(item) {
  if (item.conversationTitle) {
    return item.conversationTitle;
  }
  const conversation = conversations.conversations.find(entry => entry.id === item.conversationId);
  return conversation?.title || item.conversationId || '未知会话';
}

function favoriteText(item) {
  return item.text || '';
}

function isImageFavorite(item) {
  return item?.type === 'image' && item?.attachment?.url;
}

function isFileFavorite(item) {
  return item?.type === 'file' && item?.attachment?.url;
}

function favoriteCopyText(item) {
  if (!item) {
    return '';
  }
  if (item.text) {
    return item.text;
  }
  if (item.recalled) {
    return item.recallStatus || '已撤回';
  }
  if (isImageFavorite(item)) {
    return item.attachment.url;
  }
  if (isFileFavorite(item)) {
    return `${item.attachment.name || '文件'} ${item.attachment.url}`.trim();
  }
  return item.preview || '';
}

function formatFileSize(size) {
  const bytes = Number(size || 0);
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(bytes >= 10 * 1024 ? 0 : 1)} KB`;
  }
  return bytes > 0 ? `${bytes} B` : '';
}

function formatFavoriteTime(timestamp) {
  const value = Number(timestamp || 0);
  if (!value) {
    return '';
  }
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(value);
}

async function copyFavorite() {
  clearFeedback();
  const text = favoriteCopyText(favorite.value || {}).trim();
  if (!text) {
    error.value = '没有可复制的文字内容';
    return;
  }
  try {
    await writeClipboard(text);
    notice.value = '已复制';
  } catch (copyError) {
    error.value = '复制失败';
  }
}

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const element = document.createElement('textarea');
  element.value = text;
  element.setAttribute('readonly', 'readonly');
  element.style.position = 'fixed';
  element.style.left = '-9999px';
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  document.body.removeChild(element);
}

function openConversation() {
  clearFeedback();
  const item = favorite.value;
  if (!item?.conversationId) {
    error.value = '缺少原会话信息';
    return;
  }
  const conversation = conversations.conversations.find(entry => entry.id === item.conversationId);
  if (!conversation) {
    error.value = '原会话暂未加载，请刷新会话后再试';
    conversations.refreshRemoteConversations();
    return;
  }
  navigation.showMessages();
  conversations.setActiveConversation(item.conversationId);
  window.setTimeout(() => {
    conversations.focusMessage({
      id: item.id || item.key,
      messageUid: item.messageUid || item.key
    });
  }, 450);
}

function removeFavorite() {
  clearFeedback();
  const key = favorite.value?.key || '';
  if (!key) {
    return;
  }
  conversations.removeFavorite(key);
  notice.value = '已取消收藏';
}
</script>
